import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Avatar,
    Grid,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { sxClsx } from "utils/styleClsx";
import React, { useState } from "react";
import { observer } from "mobx-react";
import AcxButton from "components/UI/AcxButton";
import { useStore } from "utils/useStore";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import { PermissionSetter } from "./PermissionSetter";
import { UserWithFullName } from "./constants";
import { useCurrentDashboard } from "../../../useCurrentDashboard";
import { AuthStore } from "stores/AuthStore";
import { IDashboardUserPermission } from "components/Signals/SignalsModels";

// Component for sharing the dashboard with specific people
// Has the autocomplete input for selecting users and setting their permissions
export const ShareWithPeople = observer(({
    userPermissions,
    setUserPermissions,
    users,
    usersLoading,
}: {
    userPermissions: IDashboardUserPermission[];
    setUserPermissions: (permissions: IDashboardUserPermission[]) => void;
    users: UserWithFullName[] | null | undefined;
    usersLoading: boolean;
}) => {
    const currentDashboard = useCurrentDashboard();
    const authStore = useStore(AuthStore);
    const theme = useTheme();

    const [permissionToSet, setPermissionToSet] = useState<ReportAccessType>(
        ReportAccessType.View,
    );
    const [selectedUsers, setSelectedUsers] = useState<UserWithFullName[]>([]);
    const [shareLoading, setShareLoading] = useState(false);

    const handleShare = async () => {
        if (!currentDashboard || selectedUsers.length === 0) return;
        setShareLoading(true);
        const userIds = selectedUsers
            .map((user) => user.userId)
            .filter((id) => id !== undefined);
        try {
            const updatedPermissions =
                await currentDashboard.updateDashboardUserPermissions({
                    userIds,
                    permissionToSet,
                });
            setUserPermissions(updatedPermissions);
            setSelectedUsers([]);
            setShareLoading(false);
        } catch (e) {
            setShareLoading(false);
        }
    };

    return (
        <Grid sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                }}
            >
                <Typography
                    sx={{
                        color: theme.palette.gray[700],
                        fontSize: "14px",
                        fontWeight: theme.typography.fontWeightSemibold,
                        lineHeight: "20px",
                    }}
                >
                    Share with People
                </Typography>
                <Typography>
                    These permissions take priority over the Org-Wide Access
                </Typography>
            </Grid>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                }}
                id="share-with-input"
            >
                <Autocomplete
                    multiple
                    options={
                        users?.filter(
                            (user) =>
                                user.userId !== authStore.effectiveUserId,
                        ) ?? []
                    }
                    loading={usersLoading}
                    value={selectedUsers}
                    onChange={(_, newValue) => {
                        setSelectedUsers(newValue as UserWithFullName[]);
                    }}
                    getOptionLabel={(option) => option.fullName}
                    isOptionEqualToValue={(option, value) =>
                        option.userId === value.userId
                    }
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => {
                        if (selected) return null;
                        const alreadyAdded = userPermissions.some(
                            (permission) => permission.userId === option.userId,
                        );
                        return (
                            <OptionComponent
                                liProps={props}
                                user={option}
                                alreadyAdded={alreadyAdded}
                            />
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const tagProps = getTagProps({ index });
                            return (
                                <Grid
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor:
                                            theme.palette.primary[100],
                                        color: theme.palette.primary.main,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.5,
                                        mr: 0.5,
                                    }}
                                    {...tagProps}
                                >
                                    <Avatar
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            fontSize: 12,
                                            backgroundColor:
                                                theme.palette.primary[100],
                                            color: theme.palette.primary.main,
                                            mr: 0.5,
                                        }}
                                    >
                                        {option.fullName.charAt(0)}
                                    </Avatar>
                                    <Typography sx={{ fontSize: 12 }}>
                                        {option.fullName}
                                    </Typography>
                                    <span
                                        style={{
                                            marginLeft: 4,
                                            cursor: "pointer",
                                            fontWeight: 700,
                                            color: theme.palette.gray[500],
                                            fontSize: 14,
                                            padding: "0 4px",
                                            userSelect: "none",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Remove this user from selectedUsers
                                            setSelectedUsers((prev) =>
                                                prev.filter(
                                                    (u) =>
                                                        u.userId !==
                                                        option.userId,
                                                ),
                                            );
                                        }}
                                        aria-label={`Remove ${option.fullName}`}
                                    >
                                        ×
                                    </span>
                                </Grid>
                            );
                        })
                    }
                    fullWidth
                    renderInput={(params) => (
                        <AutocompleteInput
                            {...params}
                            permissionToSet={permissionToSet}
                            setPermissionToSet={setPermissionToSet}
                        />
                    )}
                />
                <AcxButton
                    id="share-with-people-button"
                    variant="contained"
                    onClick={handleShare}
                    sx={{ height: "40px" }}
                    loading={shareLoading}
                >
                    Share
                </AcxButton>
            </Grid>
        </Grid>
    );
});

const AutocompleteInput = ({
    permissionToSet,
    setPermissionToSet,
    ...params
}: {
    permissionToSet: ReportAccessType;
    setPermissionToSet: (value: ReportAccessType) => void;
} & AutocompleteRenderInputParams) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = React.useState(false);
    return (
        <Grid
            sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                border: `1px solid ${
                    isFocused
                        ? theme.palette.primary.main
                        : theme.palette.lightgrayBorder.main
                }`,
                borderRadius: 1,
                alignItems: "start",
                backgroundColor: theme.palette.white.main,
                transition: "border-color 0.2s",
            }}
        >
            <TextField
                {...params}
                id="share-with-people-input"
                variant="outlined"
                placeholder="Select users"
                InputProps={{
                    ...params.InputProps,
                    endAdornment: null,
                    onFocus: (e) => {
                        setIsFocused(true);
                    },
                    onBlur: (e) => {
                        setIsFocused(false);
                    },
                }}
                sx={{
                    minHeight: 32,
                    "& .MuiOutlinedInput-root": {
                        minHeight: 32,
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingRight: 0,
                    },
                    "& .MuiOutlinedInput-input": {
                        paddingTop: 0.5,
                        paddingBottom: 0.5,
                        fontSize: 14,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                    },
                }}
            />
            <Grid sx={{ pr: 1, pt: 0.5 }}>
                <PermissionSetter
                    permissionToSet={permissionToSet}
                    setPermissionToSet={setPermissionToSet}
                    id="share-with-people-permission-setter"
                />
            </Grid>
        </Grid>
    );
};

const OptionComponent = ({
    liProps,
    user,
    alreadyAdded,
}: {
    liProps: React.HTMLAttributes<HTMLLIElement>;
    user: UserWithFullName;
    alreadyAdded: boolean;
}) => {
    const theme = useTheme();
    const [hovering, setHovering] = React.useState(false);
    if (!user) {
        return null;
    }

    return (
        <li
            {...liProps}
            key={user.userId}
            onClick={alreadyAdded ? undefined : (e) => liProps.onClick?.(e)}
            style={{
                cursor: alreadyAdded ? "not-allowed" : "pointer",
                ...liProps.style,
                backgroundColor: hovering
                    ? theme.palette.primary.light
                    : "transparent",
            }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            id={`share-with-user-${user.userName}`}
        >
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                }}
            >
                <Grid sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    <Avatar
                        sx={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: theme.palette.primary[100],
                            color: theme.palette.primary.main,
                        }}
                    >
                        {user.fullName.charAt(0)}
                    </Avatar>
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography
                            sx={sxClsx(
                                {
                                    color: theme.palette.gray[700],
                                    fontSize: "14px",
                                    fontWeight:
                                        theme.typography.fontWeightSemibold,
                                },
                                alreadyAdded && {
                                    color: theme.palette.gray[400],
                                },
                            )}
                        >
                            {user.fullName}
                        </Typography>
                        <Typography
                            sx={sxClsx(
                                {
                                    color: theme.palette.gray[500],
                                    fontSize: "12px",
                                },
                                alreadyAdded && {
                                    color: theme.palette.gray[400],
                                },
                            )}
                        >
                            {user.email}
                        </Typography>
                    </Grid>
                </Grid>
                {alreadyAdded ? (
                    <Typography
                        sx={{
                            color: theme.palette.gray[400],
                        }}
                    >
                        Already added
                    </Typography>
                ) : null}
            </Grid>
        </li>
    );
};
